import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { makeStyles } from '@material-ui/core/styles';
import withSizes from 'react-sizes';
import LoadingIndicator from 'components/LoadingIndicator';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import ListTitle from 'components/ListTitle';
import useContentfulLocale from 'components/useContentfulLocale';
import useSiteCopySelector from 'components/useSiteCopySelector';
import ctf from 'utils/contentfulService';
import PaddedGrid from 'components/PaddedGrid';
import Carousel from 'components/ResourcesCarousel';
import ResourceItemSelector from 'components/ResourceItemSelector';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 60,
  },
  viewAllBtnWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  viewAllBtn: {
    padding: '0 10%',
    height: 46,
    background: 'white',
    border: '1px solid #0061A0',
    color: '#0061A0',
    fontSize: 15,
    fontFamily: 'PoppinsSemiBold',
  },
  carouselContainer: {
    position: 'relative',
    display: 'block',
    [theme.breakpoints.up('sm')]: {
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
    },
  },
}));

const queryFn = async (_key, orgId, localeFilters, currentServiceId) => {
  const response = await ctf.getEntries({
    content_type: 'services',
    'fields.reviewStatus': 'Accepted',
    'fields.organization.sys.id': orgId,
    ...(currentServiceId ? { 'sys.id[ne]': currentServiceId } : {}),
    ...localeFilters,
  });
  return _get(response, 'items');
};

const ServicesByOrganization = ({
  organization,
  currentServiceId,
  isMobile,
  isTablet,
  title,
}) => {
  const classes = useStyles();
  const [resourceItemPageSiteCopy] = useSiteCopySelector([
    'resource-item-page',
  ]);

  const ctfLocaleFilter = useContentfulLocale();
  const queryInfo = useQuery(
    organization && [
      'services-by-organization',
      organization.sys.id,
      ctfLocaleFilter,
      currentServiceId,
    ],
    queryFn,
  );

  const { status, data } = queryInfo;
  const processing = status === 'loading';

  if (_isEmpty(data)) return null;
  const finalTitle =
    title ||
    _get(resourceItemPageSiteCopy, 'pageCopy.otherServicesOfOrgHeader', '');

  return (
    <PaddedGrid container spacing={2} className={classes.root}>
      <PaddedGrid item xs={12}>
        <ListTitle title={finalTitle} />
      </PaddedGrid>
      {processing ? (
        <LoadingIndicator />
      ) : (
        <PaddedGrid item xs={12} className={classes.carouselContainer}>
          <Carousel
            bigArrow={isMobile || isTablet}
            slidesToSlide={isMobile ? 1 : isTablet ? 2 : 4}
            swipeable
            responsive={{
              desktop: {
                breakpoint: {
                  max: 3000,
                  min: 1024,
                },
                items: 4,
                partialVisibilityGutter: 40,
              },
              mobile: {
                breakpoint: {
                  max: 480,
                  min: 0,
                },
                items: 1,
                partialVisibilityGutter: 30,
              },
              tablet: {
                breakpoint: {
                  max: 1024,
                  min: 480,
                },
                items: 2,
                partialVisibilityGutter: 30,
              },
            }}
          >
            {data.map(item => (
              <ResourceItemSelector
                key={item.sys.id}
                item={{
                  ...item.fields,
                  id: item.sys.id,
                  type: 'services',
                }}
              />
            ))}
          </Carousel>
        </PaddedGrid>
      )}
    </PaddedGrid>
  );
};

ServicesByOrganization.propTypes = {
  organization: PropTypes.object,
  currentServiceId: PropTypes.string,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  title: PropTypes.string,
};
const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
  isTablet: width <= 1024,
});

export default withSizes(mapSizesToProps)(ServicesByOrganization);
